import { camelCase } from "change-case";
import { marked } from "marked";
import generateToken from "@utils/generateToken";
import { lenientEmphasis } from "@utils/markedExtensions";

marked.use({
  ...lenientEmphasis(),
  renderer: {
    link(href, title, text) {
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
    },
  },
});

export function capitalize(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function isEmpty(obj) {
  return [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;
}

export function set(obj, path, value) {
  // Regex explained: https://regexr.com/58j0k
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);

  pathArray.reduce((acc, key, i) => {
    if (acc[key] === undefined) acc[key] = {};
    if (i === pathArray.length - 1) acc[key] = value;
    return acc[key];
  }, obj);
}

export const get = (obj, path, defaultValue = undefined) => {
  const travel = (regexp) =>
    String.prototype.split
      .call(path, regexp)
      .filter(Boolean)
      .reduce((res, key) => (res !== null && res !== undefined ? res[key] : res), obj);
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
  return result === undefined || result === obj ? defaultValue : result;
};

const countAsLetter = (index) => {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let string = [];
  let n = index + 1;
  let rem = 0;

  while (n > 0) {
    rem = n % 26;

    if (rem === 0) {
      string.push("Z");
      n = n / 26 - 1;
    } else {
      string.push(alphabet[rem - 1]);
      n = Math.floor(n / 26);
    }
  }
  return string.reverse().join("");
};

export function itemLetter(index) {
  return countAsLetter(index);
}

export const keys = (object = {}) => Object.keys(object);

const camelizeKey = (key) => camelCase(key);

export const camelizeObject = (obj = {}) => {
  Object.keys(obj).forEach((key) => {
    const item = obj[key];
    delete obj[key];
    const newKey = camelizeKey(key);
    obj[newKey] = item;
    if (Array.isArray(item)) {
      camelizeArray(item);
    } else if (typeof item === "object" && item !== null) {
      camelizeObject(item);
    }
  });

  return obj;
};

export const camelizeArray = (arr = []) =>
  arr.map((item) => (typeof item === "object" && item !== null ? camelizeObject(item) : item));

export const uniq = (array) => [...new Set(array)];

export const uniqBy = (arr, iteratee) => {
  if (typeof iteratee === "string") {
    const prop = iteratee;
    iteratee = (item) => item[prop];
  }

  return arr.filter((x, i, self) => i === self.findIndex((y) => iteratee(x) === iteratee(y)));
};

// Copy an object while removing its reactivity
export const deepCopy = (obj = {}) => JSON.parse(JSON.stringify(obj));

// sometimes emojis add a blank line at the top of the message
export const trimBlankLines = (text) => {
  if (text.startsWith("\n")) {
    return text.replace("\n", "");
  }
  return text;
};

export const renderToHtml = (markdown = "", removeLineBreaks = false) => {
  if (!markdown) return;
  let text = trimBlankLines(markdown);

  // Check for complex markdown elements like headings or lists
  const isSimpleText = !/^[#\-*]/m.test(text) && !/\n{2,}/.test(text);

  if (isSimpleText) {
    const replacement = removeLineBreaks ? "" : "<br>";
    text = text.replace(/\n/g, replacement);
    return marked.parseInline(text);
  } else {
    return text
      .split(/\n\n/g)
      .map((text) => marked.parse(text))
      .join("\n<br>\n");
  }
};

export const removeHtmlMarkdownAndLineBreak = (text) => {
  if (!text) return "";

  text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1");
  text = text.replace(/(\*{2})([^*]*)(\*{2})/g, "$2");
  text = text.replace(/(_)([^_]*)(_)/g, "$2");
  text = text.replace(/\n/g, " ");
  return text;
};

// Plain text of an HTML fragment, tags dropped. Parses rather than regexing so
// entities decode and nested markup collapses correctly.
export const stripHtml = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").trim();
};

// Plain text of an HTML fragment's first block element — the "title line" of rich
// text whose later blocks are irrelevant (list labels, sidebar entries).
export const firstBlockText = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  const first = div.querySelector("p, h1, h2, h3, h4, li") || div;
  return (first.textContent || "").trim();
};

// Truncates a string to `maxLength` characters and appends an ellipsis when it
// overflows. Use it where CSS truncation (truncate / line-clamp) can't apply,
// e.g. SVG <text> labels which would otherwise be clipped without a "…".
export const truncateText = (text, maxLength = 30) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
};

//convert any range (min,max) as object to range as array
export const normalizeRange = (range) => {
  const values = Object.values(range);
  return values.sort((a, b) => a - b);
};

//return if two ranges intersect
export const rangesIntersect = (range1, range2) => {
  const [min1, max1] = normalizeRange(range1);
  const [min2, max2] = normalizeRange(range2);
  return Math.max(min1, min2) <= Math.min(max1, max2);
};

export const downloadBlob = (blob, response, fallbackName) => {
  const disposition = response?.headers.get("Content-Disposition") || "";
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = disposition.match(/filename="?([^";]+)"?/);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = utf8Match ? decodeURIComponent(utf8Match[1]) : asciiMatch ? asciiMatch[1] : fallbackName;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const stripTokens = (elements) => {
  if (!elements) return [];
  return elements.map((element) => {
    return { ...element, id: null, token: generateToken() };
  });
};

export const areEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const highlightKeywords = (text) => {
  return text.replace(/\*\*(.+?)\*\*/g, '<b class="heavy">$1</b>');
};
